import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, ArrowLeft, KeyRound } from 'lucide-react';
import api from '../../api/axios';
import styles from './ResetPassword.module.css';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();
  const { email, code } = location.state || {};

  // Redirect if accessed directly without email/code
  React.useEffect(() => {
    if (!email || !code) {
      navigate('/forgot-password');
    }
  }, [email, code, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== passwordConfirmation) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/reset-password', {
        email,
        token: code,
        password,
        password_confirmation: passwordConfirmation
      });

      setSuccess('Your password has been reset successfully! Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      console.error('Reset password error:', err);
      if (err.response?.status === 422) {
        setError(err.response.data.message || 'Validation failed. Please check your password.');
      } else {
        setError('Something went wrong. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles['reset-password-page']}>
      <div className={styles['reset-password-card']}>
        <div className={styles['card-header-banner']}>
          <h1 className={styles['title']}>Reset Password</h1>
        </div>

        <div className={styles['card-content']}>
          <p className={styles['subtitle']}>
            Almost there! Create a new secure password for your account.
          </p>

          {error && <div className={styles['error-message']}>{error}</div>}
          {success && <div className={styles['success-message']}>{success}</div>}

          {!success && (
            <form onSubmit={handleSubmit}>
              <div className={styles['form-group']}>
                <label className={styles['form-label']} htmlFor="password">
                  New Password
                </label>
                <div className={styles['input-wrapper']}>
                  <span className={styles['input-icon']}>
                    <Lock size={18} />
                  </span>
                  <input
                    id="password"
                    className={styles['form-input']}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className={styles['password-toggle']}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className={styles['form-group']}>
                <label className={styles['form-label']} htmlFor="confirm-password">
                  Confirm New Password
                </label>
                <div className={styles['input-wrapper']}>
                  <span className={styles['input-icon']}>
                    <Lock size={18} />
                  </span>
                  <input
                    id="confirm-password"
                    className={styles['form-input']}
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className={styles['password-toggle']}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button type="submit" className={styles['submit-btn']} disabled={loading}>
                {loading ? 'Resetting Password...' : (
                  <>
                    Reset Password
                    <KeyRound size={18} />
                  </>
                )}
              </button>
            </form>
          )}

          <hr className={styles['divider']} />

          <Link to="/login" className={styles['back-link']}>
            <ArrowLeft size={16} /> Back to Login
          </Link>
        </div>
      </div>

    </div>
  );
};

export default ResetPassword;
